import { createSelector } from 'reselect';

import { convertListToObjectBy } from 'utils/fn';
import { createContextSelector, createReduceSelector, connectNested } from 'utils/redux';
import { createMarkupReduceSelector } from 'utils/markup';
import { getBasketProductIds } from 'store/common/basket/selectors';

import {
  SharedZoneBlocksContext,
  SharedZoneBannerBlockBannersContext,
  SharedZoneSliderProductsContext,
} from './contexts';
import { getSharedZonePageData } from '../selectors';
import { cleanEmptyKeys } from './helpers';

/**
 * `blocks` is general container for all data related to promo blocks
 * the actual block data is contained within `list` array
 */
const getSharedZoneBlocksData = (state) => getSharedZonePageData(state).blocks || {};
const getSharedZoneBlocksList = (state) => getSharedZoneBlocksData(state).list || [];

/**
 * common data, not meant to be used straight away, instead enhances blocks content
 */
const getSharedZoneBlocksCommonData = (state) => getSharedZoneBlocksData(state).common || {};

/**
 * Following section contains common selectors for data fields available on all promo blocks
 */
export class SharedZoneBlocks {
  static connect = (...args) => connectNested([SharedZoneBlocksContext], ...args);

  static getBlockIds = createSelector(getSharedZoneBlocksList, (list) => list.map(({ id }) => id));

  static getData = createContextSelector(SharedZoneBlocksContext)(
    getSharedZoneBlocksList,
    convertListToObjectBy('id'),
  );

  static getType = createReduceSelector(SharedZoneBlocks.getData, (item) => item.type);

  static getId = createReduceSelector(SharedZoneBlocks.getData, (item) => item.id);

  static getContent = createReduceSelector(SharedZoneBlocks.getData, (item) =>
    cleanEmptyKeys(item.content || {}),
  );

  static getHeadline = createReduceSelector(SharedZoneBlocks.getContent, (item) => item.headline);

  static getHeadlineTagName = createReduceSelector(
    SharedZoneBlocks.getContent,
    (item) => item.headlineTagName,
  );

  static getDescription = createReduceSelector(
    SharedZoneBlocks.getContent,
    (item) => item.description,
  );

  static getDescriptionList = createReduceSelector(
    SharedZoneBlocks.getContent,
    (item) => item.descriptionList,
  );
}

export class SharedZoneBannersBlocks {
  static getCommonData = (state) => getSharedZoneBlocksCommonData(state).banners || {};

  static getContent = createReduceSelector(
    SharedZoneBannersBlocks.getCommonData,
    SharedZoneBlocks.getContent,
    (commonData, data) => ({
      ...commonData,
      ...data,
    }),
  );

  static getHeadline = createReduceSelector(SharedZoneBlocks.getContent, (item) => item.headline);

  static getUrl = createReduceSelector(SharedZoneBannersBlocks.getContent, (item) => item.url);

  static getImageUrl = createReduceSelector(
    SharedZoneBannersBlocks.getContent,
    (item) => item.imageUrl,
  );

  static getBanners = createReduceSelector(
    SharedZoneBannersBlocks.getContent,
    (item) => item.banners || [],
  );

  static getBannerIdsList = createReduceSelector(SharedZoneBannersBlocks.getBanners, (list) =>
    list.map((item) => item.bannerId),
  );
}

export class SharedZoneBannerBlocksBanners {
  static connect = (...args) =>
    connectNested([SharedZoneBlocksContext, SharedZoneBannerBlockBannersContext], ...args);

  static getBannerData = createContextSelector(SharedZoneBannerBlockBannersContext)(
    SharedZoneBannersBlocks.getBanners,
    convertListToObjectBy('bannerId'),
  );

  static getBannerTitle = createReduceSelector(
    SharedZoneBannerBlocksBanners.getBannerData,
    (item) => item.title,
  );

  static getBackgroundImageUrl = createReduceSelector(
    SharedZoneBannerBlocksBanners.getBannerData,
    (item) => item.backgroundImageUrl,
  );

  static getUrl = createReduceSelector(
    SharedZoneBannerBlocksBanners.getBannerData,
    (item) => item.url,
  );

  static getOpenInNewTabFlag = createReduceSelector(
    SharedZoneBannerBlocksBanners.getBannerData,
    (item) => item.openInNewTab,
  );

  static getIsBannerFullscreen = createReduceSelector(
    SharedZoneBannerBlocksBanners.getBannerData,
    (item) => item.isFullScreen,
  );

  static getBannerMarkup = createMarkupReduceSelector(
    createReduceSelector(SharedZoneBannerBlocksBanners.getBannerData, (item) => item.markup),
  );
}

export class SharedZonePromoBlocks {
  static getCommonData = (state) => getSharedZoneBlocksCommonData(state).promo || {};

  static getContent = createReduceSelector(
    SharedZonePromoBlocks.getCommonData,
    SharedZoneBlocks.getContent,
    (commonData, data) => ({
      ...commonData,
      ...data,
    }),
  );

  static getImageUrl = createReduceSelector(
    SharedZonePromoBlocks.getContent,
    (item) => item.imageUrl,
  );

  static getUrl = createReduceSelector(SharedZonePromoBlocks.getContent, (item) => item.url);

  static getAlign = createReduceSelector(SharedZonePromoBlocks.getContent, (item) => item.align);

  static getButtonText = createReduceSelector(
    SharedZonePromoBlocks.getContent,
    (item) => item.buttonText,
  );

  static getButtonIsHidden = createReduceSelector(
    SharedZonePromoBlocks.getContent,
    (item) => item.hiddenButton,
  );

  static getDescription = createReduceSelector(
    SharedZonePromoBlocks.getContent,
    (item) => item.description,
  );

  static getIsPrimary = createReduceSelector(
    SharedZonePromoBlocks.getContent,
    (item) => item.isPrimary,
  );

  static getMarkup = createReduceSelector(SharedZonePromoBlocks.getContent, (item) => item.markup);

  static getList = createReduceSelector(SharedZonePromoBlocks.getContent, (item) => item.list);
}

export class SharedZoneSliderBlocks {
  static getCommonContent = (state) => getSharedZoneBlocksCommonData(state).slider || {};

  static getContent = createReduceSelector(
    SharedZoneSliderBlocks.getCommonContent,
    SharedZoneBlocks.getContent,
    (commonData, data) => ({
      ...commonData,
      ...data,
    }),
  );

  static getReferenceText = createReduceSelector(
    SharedZoneSliderBlocks.getContent,
    (item) => item.referenceTemplate,
  );

  static getBlockHasReferenceText = createReduceSelector(
    SharedZoneSliderBlocks.getReferenceText,
    (item) => !!item,
  );

  static getUrl = createReduceSelector(SharedZoneSliderBlocks.getContent, (item) => item.url);

  static getListHeadline = createReduceSelector(
    SharedZoneSliderBlocks.getContent,
    (item) => item.headline,
  );

  static getProductList = createReduceSelector(
    SharedZoneSliderBlocks.getContent,
    SharedZoneBlocks.getHeadline,
    (item) => (item.list || []).filter((itm) => itm.price),
  );

  static getProductIdsList = createReduceSelector(SharedZoneSliderBlocks.getProductList, (list) =>
    list.map(({ productId }) => productId),
  );

  static getProductRequestData = createReduceSelector(
    SharedZoneSliderBlocks.getContent,
    (item) => item.requestData,
  );

  static getPosition = createReduceSelector(
    SharedZoneSliderBlocks.getContent,
    (item) => item.position,
  );
}

export class SharedZoneSliderProducts {
  static connect = (...args) =>
    connectNested([SharedZoneBlocksContext, SharedZoneSliderProductsContext], ...args);

  static getCommonData = (state) => getSharedZoneBlocksCommonData(state).sliderListItem || {};

  static getRawData = createContextSelector(SharedZoneSliderProductsContext)(
    SharedZoneSliderBlocks.getProductList,
    convertListToObjectBy('productId'),
  );

  static getData = createReduceSelector(
    SharedZoneSliderProducts.getCommonData,
    SharedZoneSliderProducts.getRawData,
    (commonData, data) => ({
      ...commonData,
      ...data,
    }),
  );

  static getImageUrl = createReduceSelector(
    SharedZoneSliderProducts.getData,
    (item) => item.imageUrl,
  );

  static getProductId = createReduceSelector(SharedZoneSliderProducts.getData, (item) =>
    parseInt(item.productId, 10),
  );

  static getIsInBasket = createSelector(
    SharedZoneSliderProducts.getProductId,
    getBasketProductIds,
    (productId, addedItems) => addedItems.indexOf(productId) !== -1,
  );

  static getSlug = createReduceSelector(SharedZoneSliderProducts.getData, (item) => item.slug);

  static getTitle = createReduceSelector(SharedZoneSliderProducts.getData, (item) => item.title);

  static getUrl = createReduceSelector(SharedZoneSliderProducts.getData, (item) => item.url);

  static getIsPreorder = createReduceSelector(
    SharedZoneSliderProducts.getData,
    (item) => item.isPreorder,
  );

  static getPriceData = createReduceSelector(
    SharedZoneSliderProducts.getData,
    (item) => item.price,
  );

  static getOldPrice = createReduceSelector(
    SharedZoneSliderProducts.getPriceData,
    (item) => item.oldValue,
  );

  static getPrice = createReduceSelector(
    SharedZoneSliderProducts.getPriceData,
    (item) => item.value,
  );

  static getDiscount = createReduceSelector(
    SharedZoneSliderProducts.getOldPrice,
    SharedZoneSliderProducts.getPrice,
    (oldPrice, price) => Math.max(oldPrice - price, 0) || 0,
  );

  static getButtonText = createReduceSelector(
    SharedZoneSliderProducts.getIsPreorder,
    SharedZoneSliderProducts.getData,
    (isPreorder, { preorderButtonText, purchaseButtonText }) =>
      isPreorder ? preorderButtonText : purchaseButtonText,
  );

  static getInBasketButtonText = createReduceSelector(
    SharedZoneSliderProducts.getData,
    ({ isInBasketButtonText }) => isInBasketButtonText,
  );
}

export class SharedZoneFullScreenBanner {
  static getContent = createReduceSelector(SharedZoneBlocks.getData, (item) =>
    cleanEmptyKeys(item.content || {}),
  );

  static getUrl = createReduceSelector(SharedZoneFullScreenBanner.getContent, (item) => item.url);

  static getImageUrl = createReduceSelector(
    SharedZoneFullScreenBanner.getContent,
    (item) => item.imageUrl,
  );

  static getMobileImageUrl = createReduceSelector(
    SharedZoneFullScreenBanner.getContent,
    (item) => item.mobileImageUrl,
  );

  static getMarkup = createReduceSelector(
    SharedZoneFullScreenBanner.getContent,
    (item) => item.markup,
  );

  static getOpenInNewTab = createReduceSelector(
    SharedZoneFullScreenBanner.getContent,
    (item) => item.openInNewTab,
  );

  static getImageBackGround = createReduceSelector(
    SharedZoneFullScreenBanner.getContent,
    (item) => item.imageBackGround,
  );
}

export class SharedZoneInfographic {
  static getList = createReduceSelector(SharedZoneBlocks.getContent, (item) => item.icons || []);

  static getListHasMarkup = createReduceSelector(SharedZoneInfographic.getList, (list) =>
    list.reduce((result, item) => result || !!item.popupMarkup, false),
  );

  /* TODO: use the whole array after frameswitcher added */
  static getGoods = createReduceSelector(SharedZoneBlocks.getContent, (item) =>
    (item.list || []).filter((itm, i) => i === 0),
  );
}

export class SharedZoneGoodsDayBlocks {
  static getProductListRaw = createReduceSelector(SharedZoneBlocks.getContent, (item) =>
    (item.list || []).filter((itm) => itm.productId && itm.price),
  );
}

export class SharedZoneCustomSlug {
  static getSlug = createReduceSelector(SharedZoneBlocks.getContent, (item) => item.customSlug);

  static getWidgetData = createReduceSelector(
    SharedZoneBlocks.getContent,
    (item) => item.widgetData || {},
  );

  /* widget-specific selectors */
  static getUseSubscribeWidgetFlag = createReduceSelector(
    SharedZoneBlocks.getContent,
    (item) => item.useSubscribe || {},
  );

  static getSubscribeWidgetData = createReduceSelector(
    SharedZoneBlocks.getContent,
    (item) => item.subscribeWidgetData || {},
  );
}

export class SharedZoneBeautyContent {
  static getContent = createReduceSelector(SharedZoneBlocks.getData, (item) =>
    cleanEmptyKeys(item.content || {}),
  );

  static getMarkup = createReduceSelector(
    SharedZoneBeautyContent.getContent,
    (item) => item.markup,
  );

  static getAuthor = createReduceSelector(
    SharedZoneBeautyContent.getContent,
    (item) => item.author,
  );

  static getDate = createReduceSelector(SharedZoneBeautyContent.getContent, (item) => item.date);
}

export class SharedZoneArticles {
  static getContent = createReduceSelector(SharedZoneBlocks.getData, (item) =>
    cleanEmptyKeys(item.content || {}),
  );

  static getHeadline = createReduceSelector(SharedZoneArticles.getContent, (item) => item.headline);

  static getHeadlineTagName = createReduceSelector(
    SharedZoneArticles.getContent,
    (item) => item.headlineTagName,
  );

  static getList = createReduceSelector(SharedZoneArticles.getContent, (item) => item.list || []);
}

export class SharedZoneTags {
  static getContent = createReduceSelector(SharedZoneBlocks.getData, (item) =>
    cleanEmptyKeys(item.content || {}),
  );

  static getHeadline = createReduceSelector(SharedZoneTags.getContent, (item) => item.headline);

  static getHeadlineTagName = createReduceSelector(
    SharedZoneTags.getContent,
    (item) => item.headlineTagName,
  );

  static getList = createReduceSelector(SharedZoneTags.getContent, (item) => item.list || []);
}
